import React, { useState, useEffect, useMemo, useRef } from 'react';
import { FaBell } from 'react-icons/fa';
import { IoReorderThreeOutline } from "react-icons/io5";
import './Header.css';
import CookiesServices from '../../services/CookiesService';
import { FiSearch } from "react-icons/fi";
import SearchMenu from '../search-menu/SearchMenu';
import '../../colors/colors.css';
import { IoIosArrowDropdown } from "react-icons/io";
import { FaRegUserCircle } from 'react-icons/fa';
import { useNavigate } from "react-router-dom";
import { MdOutlineLockReset } from "react-icons/md";
import { BiLogOutCircle } from "react-icons/bi";
import EHubImage from '../../../src/assets/form_logo.png';
import useAgentProfile from '../../pages/AgentProfile/use-AgentProfile';
import CompletenessPopup from '../../Components/popup/completeness-popup/completenessPopup';
import Feedback from '../../Components/feedback/Feedback';
import LeaderDashboard from '../../pages/LeaderDashboard/LeaderDashboard';

const Header = ({ onToggleSidebar, currentPath }) => {
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [branch, setBranch] = useState('');
  const cookiesServices = useMemo(() => new CookiesServices(), []);
  const [openSearchMenu, setOpenSearchMenu] = useState(false);
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const { photoUrl, formData, isSameUser } = useAgentProfile();
  const [feedbackMessage, setFeedbackMessage] = useState('');
  const [feedbackType, setFeedbackType] = useState('error');
  const [showPopup, setShowPopup] = useState(true);
  const rawHasProfile = cookiesServices.get('hasProfile');
  const hasProfile = rawHasProfile === true || rawHasProfile === 'true';
  const headers = cookiesServices.getHeaders();

  useEffect(() => {
    const storedCode = cookiesServices.get('code');
    const storedBranch = cookiesServices.get('branch');
    setCode(storedCode || '');
    setBranch(storedBranch || '');
  }, [cookiesServices]);

  useEffect(() => {
    const loadName = () => {
      const storedName = cookiesServices.get('name');
      if (storedName) setName(storedName);
    };

    loadName(); // initial load
    window.addEventListener('level_changed', loadName); // listen for updates

    return () => {
      window.removeEventListener('level_changed', loadName);
    };
  }, [cookiesServices]);

  const handleLogout = async () => {
    const accessToken = cookiesServices.get('AccessToken');
    cookiesServices.remove('AccessToken');
    cookiesServices.remove('UserName');
    cookiesServices.remove('Level');
    cookiesServices.remove('code');
    cookiesServices.remove('session_state');
    cookiesServices.remove('name');
    cookiesServices.remove('branch');
    try {
      const url = cookiesServices.toUrl('v1', `/logout`)
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'AuthenticationToken': accessToken
        },
      });
    } catch (error) {
    }
    window.location.href = `${process.env.REACT_APP_LOGIN_URL}&prompt=login`;
  };

  const handleSearchMenu = () => {
    setOpenSearchMenu(!openSearchMenu);
  };

  const closeSearchMenu = () => {
    setOpenSearchMenu(false);
  };

  const formatPath = (path) => {
    if (!path) return '';

    const customNames = {
      anbpLeaderboard: 'ANBP Leaderboard',
      MDRTDashboard: 'MDRT Dashboard',
      MDRTLeaderboard: 'MDRT Leaderboard',
      dashboard: 'Agent Dashboard',
    };

    const pathParts = path.replace(/^\/+/, '').split('/');
    const lastPart = pathParts[pathParts.length - 1];

    // Return exact custom name if matched
    if (customNames[lastPart]) {
      return customNames[lastPart];
    }

    return pathParts
      .map((part) => {
        // Keep ANBP, GWP, FYP fully uppercase
        const upperCases = ['anbp', 'gwp', 'fyp'];
        if (upperCases.includes(part.toLowerCase())) {
          return part.toUpperCase();
        }

        return part
          .replace(/([a-z])([A-Z])/g, '$1 $2')
          .replace(/^./, (str) => str.toUpperCase());
      })
      .join(' ');
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleProfileClick = () => {
    setMenuOpen(false);

    if (!isSameUser) {
      setFeedbackMessage("You are not authorized to update this eProfile.");
      setFeedbackType("error");
      return;
    }

    if (!hasProfile) {
      setFeedbackMessage("Profile not found.");
      setFeedbackType("error");
      return;
    }

    navigate('/eProfile');
  };

  return (
    <>
    <div className="header">
      <div className="toggle-section">
        <IoReorderThreeOutline className="toggle" onClick={onToggleSidebar} />
        <div className="header-path">{formatPath(currentPath)}</div>
        <img src={EHubImage} alt="EHub" className="eHub-header-image" />
      </div>
      <div className="header-actions">
          <button className="header-button" onClick={handleSearchMenu}>
            <FiSearch className="header-icon" />
          </button>
          <FaBell className="header-icon" />
          <div className="user-icon">
            <img
              src={photoUrl}
              alt="User"
              className="user-image"
              style={{ cursor: 'pointer', marginRight: '10px' }}
              onClick={handleProfileClick}
            />
            {isSameUser && hasProfile && formData && showPopup && (
              <CompletenessPopup
                isFirstLogin={true} // or dynamic logic
                onRemindLater={() => setShowPopup(false)} // handle "Remind me later"
              />
            )}
            <IoIosArrowDropdown
              className="header-icon"
              onClick={() => setMenuOpen(!menuOpen)}
              style={{ cursor: 'pointer', marginRight: '10px' }}
            />
          <div className="user-info">
            <span className="code">{code || ''}</span>
            <span className="level">{branch || ''}</span>
            <span className="username">{name ? name.split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ') : ''}</span>
          </div>
        </div>
      </div>
      {menuOpen && (
          <div className="custom-menu" ref={menuRef}>
            <div 
              className="menu-item" 
              onClick={() => {
                setMenuOpen(false);

                if (!isSameUser) {
                  setFeedbackMessage("You are not authorized to update this eProfile.");
                  setFeedbackType("error");
                  return;
                }

                if (!hasProfile) {
                  setFeedbackMessage("Profile not found.");
                  setFeedbackType("error");
                  return;
                }

                navigate('/eProfile');
              }}

            > 
              <FaRegUserCircle className="menu-icon" /> Profile
            </div>
            <div
              className="menu-item"
              onClick={() => {
                setMenuOpen(false);
                navigate('/passwordReset');
              }}
            >
              <MdOutlineLockReset className="menu-icon" /> Password Reset
            </div>
            <div className="divider"></div>
            <div className="menu-item" onClick={handleLogout}>
              <BiLogOutCircle className="menu-icon" /> Logout
            </div>
          </div>
        )}
    </div>
      {openSearchMenu && <SearchMenu onClose={closeSearchMenu} />}

      {feedbackMessage && (
        <Feedback
          message={feedbackMessage}
          type={feedbackType}
          onClose={() => setFeedbackMessage('')}
        />
      )}
    </>
  );
};

export default Header;